import { stickerUrl } from "@listou/shared/stickers";

import type { BlogPost } from "../../lib/blog";
import { INLINE_CTA } from "../../lib/blog-markers";
import { no, partial, unstated, yes } from "../comparatifs/cells";

export const meilleureListeDeNaissance: BlogPost = {
  h1: "Les meilleures listes de naissance en ligne en 2026",
  seoTitle: "Meilleure liste de naissance 2026 : le comparatif des sites · Listou",
  seoDescription:
    "Listou, Mes Envies, Milirose, Kadolog, Listéa, Amazon, Aubert, Orchestra : prix, boutiques, surprise, gestion à deux, design et cagnotte. Le comparatif des listes de naissance en ligne, sources à l'appui.",
  publishedOn: "2026-09-29",
  updatedOn: "2026-09-29",
  checkedOn: "2026-09-29",
  summary:
    "Les listes de naissance en ligne se ressemblent sur l'essentiel : on y ajoute des articles, les proches réservent, personne n'offre deux fois la même poussette. Elles diffèrent sur le prix, les boutiques acceptées, la surprise (les parents voient-ils ce qui est réservé ?), la gestion à deux et le design de la page. Mes Envies et Milirose sont gratuits et acceptent toutes les boutiques. Kadolog est gratuit jusqu'à 10 réservations. Amazon, Aubert et Orchestra récompensent les achats faits dans leur catalogue. Listou, notre service, est gratuit, garde la surprise par défaut, se gère à deux et se personnalise entièrement.",
  table: {
    caption: "Huit listes de naissance en ligne, critère par critère",
    columns: [
      "Prix",
      "Boutiques",
      "Surprise par défaut",
      "Gestion à deux",
      "Personnalisation",
      "Argent",
    ],
    rows: [
      {
        name: "Listou",
        cells: [
          yes("Gratuit, sans limite de réservations"),
          yes("N'importe laquelle, par lien"),
          yes("Oui, jusqu'au lendemain de l'événement"),
          yes("Co-propriétaire"),
          yes("6 modèles, 13 palettes, 4 polices, 162 stickers, motifs"),
          partial("Lien vers votre cagnotte"),
        ],
      },
      {
        name: "Mes Envies",
        cells: [
          yes("Gratuit"),
          yes("N'importe laquelle, ou à la main"),
          no("Les réservations s'affichent dans la gestion"),
          unstated("Non décrit"),
          partial("Photo d'en-tête, couleur des boutons"),
          yes("IBAN ou lien de cagnotte, sans frais"),
        ],
      },
      {
        name: "Milirose",
        cells: [
          yes("Gratuit"),
          yes("N'importe laquelle, ou à la main"),
          partial("« Sans surprise » par défaut, trois niveaux au choix"),
          unstated("Non décrit"),
          partial("Image d'en-tête, couleurs du menu et des boutons"),
          yes("RIB ou PayPal, sans commission"),
        ],
      },
      {
        name: "Kadolog",
        cells: [
          partial("Gratuit jusqu'à 10 réservations, Premium 29 €"),
          yes("N'importe laquelle, ou à la main"),
          no("Les réservations s'affichent dans « mes cadeaux reçus »"),
          unstated("Non décrit"),
          partial("Photo de couverture, titre, fond d'écran"),
          yes("Virement ou carte à 2,4 % (Premium)"),
        ],
      },
      {
        name: "Listéa",
        cells: [
          yes("Gratuit, sans commission"),
          yes("N'importe laquelle"),
          no("Le prénom de chaque offrant est affiché"),
          unstated("Non détaillé"),
          partial("Modèles thématiques, photo de couverture"),
          yes("Cagnotte par cadeau, virement direct, sans frais"),
        ],
      },
      {
        name: "Amazon",
        cells: [
          unstated("Tarif non décrit ; 15 % de réduction au titulaire, jusqu'à 300 €"),
          partial("Catalogue Amazon, vendeurs tiers inclus"),
          partial("Secret « plusieurs semaines », si connecté"),
          yes("Cotitulaire"),
          unstated("Aucune option décrite"),
          unstated("Non décrit"),
        ],
      },
      {
        name: "Aubert",
        cells: [
          unstated("Tarif non décrit ; bon d'achat de 10 % dès 900 € d'achats"),
          partial("Produits Aubert, hors Marketplace"),
          partial("Noms visibles, sauf si l'offrant reste anonyme"),
          partial("Nom du co-parent sur la liste"),
          partial("Couverture parmi une sélection, titre, message"),
          partial("Cagnotte de 5 à 10 000 €, carte ou PayPal"),
        ],
      },
      {
        name: "Orchestra",
        cells: [
          partial("Club obligatoire : 3,99 €/mois ou 30 €/an"),
          partial("Puériculture, jouets, Prémaman"),
          unstated("Non décrit"),
          unstated("Non décrit"),
          unstated("Non décrit"),
          partial("Solde converti en carte cadeau à la clôture"),
        ],
      },
    ],
  },
  body: `
## Comment nous avons comparé

Nous avons retenu huit services parmi les plus utilisés en France : cinq listes ouvertes à toutes les boutiques (Listou, Mes Envies, Milirose, Kadolog, Listéa) et trois listes d'enseigne (Amazon, Aubert, Orchestra). Listou est notre service : nous le présentons avec les autres, et chaque information sur les autres vient de leur propre site (pages d'aide, FAQ, tarifs), listé en bas de page avec la date de consultation. Quand un site ne décrit pas une fonction, nous l'écrivons « non décrit » plutôt que de supposer qu'elle n'existe pas.

Les critères sont ceux qui comptent une fois la liste envoyée : combien elle coûte, où les proches peuvent acheter, si vous gardez la surprise, si vous pouvez la préparer à deux, à quoi elle ressemble, et comment recevoir de l'argent.

## Listou : une liste à votre image, la surprise gardée

**Listou** est gratuit, sans limite de réservations. On colle le lien d'un produit de n'importe quelle boutique : le nom, la photo et le prix sont récupérés. Vos proches réservent sans compte, le prénom et l'email sont facultatifs, et ils achètent où ils veulent.

Le mode surprise est activé dès la création : ni vous ni votre co-parent ne voyez ce qui est réservé, ni par qui, jusqu'au lendemain de la date de l'événement. Vous recevez seulement « un cadeau vient d'être réservé ». Un proche peut aussi cocher « Rester anonyme », et son nom reste caché même après la révélation.

Le co-parent devient **co-propriétaire**, avec les mêmes droits.

C'est aussi sur le design que Listou va le plus loin. D'après leurs sites, aucun des autres services comparés ne propose de choix de polices ni de stickers. Une liste de naissance s'imprime sur le faire-part et circule dans toute la famille : dans l'onglet Apparence, avec l'aperçu en direct, vous choisissez parmi **6 modèles de page** (la mise en page elle-même change), **13 palettes** et une couleur d'accent, **4 polices** de titre, et **162 stickers** dessinés en 18 thèmes, pour décorer l'en-tête ou tapisser le fond en motifs. Une photo de couverture et un emoji complètent la page. Elle se partage par lien, par **QR code** à imprimer sur le faire-part et en **visuels prêts pour Instagram**.

Sa limite : Listou ne fait transiter aucun argent. Pour les participations, vous ajoutez un lien vers votre cagnotte (Leetchi, Lydia…), mais il n'y a pas de champ pour afficher un IBAN.
${INLINE_CTA}## Mes Envies : gratuit et installé depuis 2006

**Mes Envies** est gratuit, sans limite de fonctions ni de durée, et accepte n'importe quelle boutique. Les proches réservent sans compte, avec leur nom et, en option, leur email. Le service existe depuis 2006.

Son point fort est l'argent : un champ « Compte bancaire ou lien vers cagnotte » permet d'afficher votre IBAN à chaque personne qui réserve, sans frais Mes Envies.

Sa limite est la surprise. L'email envoyé à chaque réservation ne dit pas qui offre quoi, mais l'espace de gestion affiche tous les cadeaux réservés, sans réglage pour les cacher. La personnalisation se limite à une photo d'en-tête et à la couleur des liens et boutons, et l'aide ne décrit pas de gestion à deux.

## Milirose : trois niveaux de surprise

**Milirose** est gratuit, pour le créateur comme pour les proches, et annonce plus d'un million de listes de naissance publiées. On ajoute un cadeau par son lien, par un bouton d'ajout rapide sur ordinateur ou à la main.

Milirose laisse choisir entre trois niveaux de surprise : « Sans surprise », le réglage par défaut, où vous voyez chaque réservation et son auteur ; « Demi-surprise », où vous voyez les cadeaux réservés mais pas par qui ; « Surprise totale », où vous ne voyez rien. Pensez donc à changer le réglage à la création si vous voulez être surpris.

Pour l'argent, un champ « Cagnotte » accueille votre RIB/IBAN ou votre compte PayPal, sans commission Milirose. Milirose propose aussi des occasions comme le baptême, le baby shower ou la crémaillère. Sa FAQ ne décrit pas de gestion à deux, et indique que l'adresse de livraison est visible par toutes les personnes qui consultent la liste.

## Kadolog : le paiement par carte intégré

**Kadolog**, en ligne depuis 2008, accepte n'importe quelle boutique, ainsi que les services, les expériences et la seconde main. La formule gratuite a une limite : d'après la FAQ, au-delà de 10 réservations, vos proches ne peuvent plus réserver tant que vous n'avez pas pris Premium, à 29 € par liste (Extra à 39 €).

Premium permet à vos proches de vous verser le montant des cadeaux par virement, sans frais, ou par carte, avec 2,4 % de frais. Parmi les listes ouvertes à toutes les boutiques, c'est la seule à intégrer le paiement par carte.

Côté surprise, vous êtes prévenu à chaque réservation et l'onglet « mes cadeaux reçus » affiche tous les cadeaux réservés. La FAQ ne décrit ni réglage pour les cacher, ni gestion à deux.

## Listéa : sans commission, avec cagnotte par cadeau

**Listéa** est gratuit et ne prélève aucune commission. On ajoute les articles de n'importe quelle boutique : grandes enseignes, créateurs, boutiques locales. Les proches réservent sans compte, en indiquant leur prénom. On peut ouvrir une cagnotte sur un cadeau, une poussette par exemple : les proches participent par virement, directement aux parents, sans frais. La page part d'un modèle thématique, avec photo de couverture et message de bienvenue.

Côté surprise, le prénom de chaque offrant est affiché, pour que les parents sachent de qui vient chaque cadeau. L'aide ne décrit pas de mode pour cacher les réservations aux parents.

## Amazon : la réduction de 15 %

La **liste de naissance Amazon** se remplit avec des articles trouvés par recherche sur Amazon, vendeurs tiers compris. Son avantage est net : le titulaire principal bénéficie de 15 % de réduction sur les articles éligibles, pour des commandes jusqu'à 2 000 €, soit 300 € au plus.

La liste a un cotitulaire, et on peut inviter des proches en « Lecture et modification ». Le paramètre « Ne pas me gâcher la surprise » est activé par défaut et garde les achats secrets « pendant plusieurs semaines », mais seulement quand vous êtes connecté : sans identification, la liste peut révéler une surprise.

Sa limite : seuls les articles ajoutés au panier depuis la liste sont marqués comme achetés. Un cadeau commandé depuis sa page produit n'est pas marqué comme acheté sur la liste, et un autre proche peut l'offrir à son tour.

## Aubert et Orchestra : les listes d'enseigne

**Aubert** ne demande pas d'adhérer à Aubert Family pour créer une liste. Elle se remplit avec les produits Aubert : les articles des vendeurs tiers de la Marketplace ne peuvent pas y être ajoutés. Les proches commandent sans compte, en invité, ou participent à une cagnotte de 5 € à 10 000 €, par carte ou PayPal. À la clôture, Aubert offre un bon d'achat de 10 % à partir de 900 € d'achats sur la liste. Les parents voient le nom des offrants dans l'onglet « Activité », sauf si l'un d'eux choisit de rester anonyme. La page se personnalise avec une couverture choisie parmi une sélection, un titre et un message.

**Orchestra** demande une adhésion au Club en cours de validité : 3,99 € par mois avec engagement d'un an, ou 30 € par an. La liste porte sur la puériculture, les jouets et le textile Prémaman ; le textile Orchestra en est exclu. La cagnotte se dépense sur les articles de la liste, et à la clôture, le solde devient une carte cadeau valable 12 mois. Un bon d'achat récompense aussi la liste selon son montant : 5 % de 500 à 999 €, 10 % à partir de 1 000 €.

Ces deux listes conviennent si vous comptez acheter l'essentiel chez l'enseigne : l'argent reçu y reste.

## Quelle liste choisir ?

- **Vous voulez une liste qui vous ressemble** : Listou, avec ses modèles de page, palettes, polices et stickers.
- **Vous voulez découvrir les cadeaux à la naissance** : Listou, où la surprise est gardée par défaut, ou Milirose en choisissant « Surprise totale ».
- **Vous préparez la liste à deux** : Listou (co-propriétaire) ou Amazon (cotitulaire).
- **Vous voulez recevoir des virements directs** : Mes Envies ou Milirose, qui affichent votre IBAN, ou Listéa, avec une cagnotte par cadeau.
- **Vous voulez que vos proches paient par carte sur la liste** : Kadolog, en Premium.
- **Vous achèterez l'essentiel chez une enseigne** : Amazon (15 % de réduction), Aubert (bon de 10 % dès 900 €) ou Orchestra (bon de 5 à 10 %, Club obligatoire).
- **Vos articles viennent de plusieurs boutiques** : Listou, Mes Envies, Milirose, Kadolog ou Listéa.

Pour aller plus loin, nos comparatifs détaillés opposent Listou à [Mes Envies](/comparatif/mesenvies), [Milirose](/comparatif/milirose), [Kadolog](/comparatif/kadolog) et [Amazon](/comparatif/amazon), point par point.
`,
  faq: [
    [
      "Quelle est la meilleure liste de naissance en 2026 ?",
      "Cela dépend de ce qui compte pour vous. Pour garder la surprise et préparer la liste à deux gratuitement, Listou. Pour afficher votre IBAN sans frais, Mes Envies ou Milirose. Pour le paiement par carte intégré, Kadolog en Premium. Pour la réduction de 15 % sur vos propres achats, Amazon.",
    ],
    [
      "Peut-on personnaliser le design de sa liste de naissance ?",
      "Sur Listou, entièrement : 6 modèles de page, 13 palettes et une couleur d'accent, 4 polices de titre, 162 stickers en 18 thèmes, des fonds à motifs et une photo de couverture. Mes Envies, Milirose, Kadolog, Listéa et Aubert proposent surtout une photo ou un modèle de couverture, un titre et quelques couleurs ; les pages d'aide Amazon ne décrivent pas d'option de design.",
    ],
    [
      "Quelle liste de naissance est gratuite ?",
      "Listou, Mes Envies, Milirose et Listéa sont gratuits. Kadolog a une formule gratuite, mais au-delà de 10 réservations vos proches ne peuvent plus réserver sans Premium, à 29 € par liste. La liste Orchestra demande une adhésion au Club, à 3,99 € par mois ou 30 € par an.",
    ],
    [
      "Peut-on mettre des articles de n'importe quel magasin sur sa liste de naissance ?",
      "Oui sur Listou, Mes Envies, Milirose, Kadolog et Listéa : on colle le lien du produit, ou on l'ajoute à la main. Les listes Amazon, Aubert et Orchestra se remplissent avec les articles de l'enseigne.",
    ],
    [
      "Sur quelle liste de naissance les parents ne voient-ils pas les cadeaux réservés ?",
      "Sur Listou, par défaut, jusqu'au lendemain de l'événement. Sur Milirose, en choisissant le niveau « Surprise totale ». Sur Amazon, le paramètre de surprise garde les achats secrets plusieurs semaines quand on est connecté. Sur Mes Envies et Kadolog, l'espace de gestion affiche les cadeaux réservés ; Listéa et Aubert affichent le nom des offrants, sauf anonymat choisi chez Aubert.",
    ],
    [
      "Quelle liste de naissance se gère à deux ?",
      "Listou, où le co-parent devient co-propriétaire, et Amazon, où la liste a un cotitulaire. Les aides de Mes Envies, Milirose et Kadolog ne décrivent pas de gestion à deux.",
    ],
    [
      "Comment recevoir de l'argent avec une liste de naissance ?",
      "Mes Envies et Milirose affichent votre IBAN (ou PayPal chez Milirose) aux proches, sans frais. Kadolog Premium encaisse par virement sans frais ou par carte à 2,4 %. Listéa ouvre une cagnotte par cadeau, payée par virement direct. Aubert et Orchestra ont une cagnotte à dépenser chez eux. Listou ajoute un lien vers votre cagnotte externe, sans faire transiter d'argent.",
    ],
    [
      "Les proches doivent-ils créer un compte pour offrir ?",
      "Non sur Listou, où le prénom et l'email sont même facultatifs, ni sur Mes Envies et Milirose. L'aide Amazon décrit l'achat par panier et commande sans préciser si un compte est requis.",
    ],
  ],
  sources: [
    { label: "Mes Envies : page d'accueil", url: "https://www.mesenvies.fr/" },
    {
      label: "Mes Envies : est-ce 100 % gratuit ?",
      url: "https://aide.mesenvies.fr/hc/331138012/25/est-ce-100-gratuit",
    },
    {
      label: "Mes Envies : faut-il un compte pour offrir un cadeau ?",
      url: "https://aide.mesenvies.fr/hc/331138012/50/est-ce-que-je-dois-creer-un-compte-pour-offrir-un-cadeau",
    },
    {
      label: "Mes Envies : gérer les réservations et dons libres",
      url: "https://aide.mesenvies.fr/hc/331138012/5/gerer-les-reservations-et-dons-libres",
    },
    {
      label: "Mes Envies : personnaliser l'aspect visuel de votre liste",
      url: "https://aide.mesenvies.fr/hc/331138012/7/personnaliser-laspect-visuel-de-votre-liste",
    },
    {
      label: "Mes Envies : recevoir des dons libres",
      url: "https://aide.mesenvies.fr/hc/331138012/4/recevoir-des-dons-libres-ou-des-participations-partielles",
    },
    { label: "Milirose : page d'accueil liste de naissance", url: "https://www.milirose.com/" },
    { label: "Milirose : foire aux questions", url: "https://www.milirose.com/faq.html" },
    {
      label: "Kadolog : tarifs et conditions",
      url: "https://www.kadolog.com/fr-fr/tarifs-et-conditions",
    },
    { label: "Kadolog : FAQ", url: "https://www.kadolog.com/fr-fr/faq" },
    {
      label: "Kadolog : FAQ des détenteurs d'une liste",
      url: "https://www.kadolog.com/fr-fr/faq/45203",
    },
    {
      label: "Kadolog : mentions légales et conditions générales d'utilisation",
      url: "https://www.kadolog.com/fr-fr/legal",
    },
    {
      label: "Listéa : aide",
      url: "https://www.listea.fr/aide",
    },
    {
      label: "Aubert : liste de naissance",
      url: "https://www.aubert.com/liste-de-naissance",
    },
    {
      label: "Aubert : aide de la liste de naissance",
      url: "https://www.aubert.com/aide-liste-naissance.html",
    },
    {
      label: "Aubert : conditions de la Marketplace",
      url: "https://www.aubert.com/cgu-marketplace",
    },
    { label: "Orchestra : FAQ", url: "https://fr.shop-orchestra.com/fr/FAQ.html" },
    {
      label: "Orchestra : avantages de la liste de naissance",
      url: "https://fr.shop-orchestra.com/fr/tab-liste-de-naissance.html",
    },
    {
      label: "Orchestra : Club",
      url: "https://fr.shop-orchestra.com/fr/club-orchestra.html",
    },
    {
      label: "Amazon : réduction sur la liste de naissance",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GV4FFW94JAGFL4RH",
    },
    {
      label: "Amazon : créer votre liste de naissance",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GXU26CGG4DXNYTU6",
    },
    {
      label: "Amazon : acheter un article d'une liste de naissance",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GX7RDQYYVS85LM6X",
    },
    {
      label: "Amazon : partager votre liste (lecture seule ou modification)",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GPWMKQXXSBT8NAHN",
    },
    {
      label: "Amazon : configurer les paramètres de surprise",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=G9PJNRV76JRAXDJZ",
    },
  ],
  occasion: "birth",
  inlineCta: {
    title: "Votre liste de naissance en deux minutes",
    text: "De toutes les boutiques, la surprise gardée, sans compte pour vos proches.",
    sticker: stickerUrl("bebe", "nounours"),
  },
};
